import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Plus, Trash2, Upload, ChevronDown, ChevronUp,
  DollarSign, CreditCard, Receipt, Package, Percent
} from 'lucide-react';
import uploadService from '../../../services/uploadService';

const MODES = [
  { id: 'single', name: 'Single Product', icon: Package, desc: 'One product checkout' },
  { id: 'multi', name: 'Multi-Product', icon: DollarSign, desc: 'Cart with multiple items' },
  { id: 'dynamic', name: 'Dynamic Pricing', icon: Percent, desc: 'Flexible pricing' },
];

const PAYMENT_METHODS = [
  { id: 'stripe', name: 'Credit/Debit Card', icon: CreditCard, desc: 'Via Stripe' },
  { id: 'paypal', name: 'PayPal', icon: DollarSign, desc: 'PayPal checkout' },
  { id: 'manual', name: 'Manual/Invoice', icon: Receipt, desc: 'Pay later' },
];

const DEFAULT_FIELDS = [
  { id: 'email', name: 'email', label: 'Email', type: 'email', required: true },
  { id: 'name', name: 'name', label: 'Full Name', type: 'text', required: true },
];

const OPTIONAL_FIELDS = [
  { id: 'phone', name: 'phone', label: 'Phone', type: 'phone' },
  { id: 'company', name: 'company', label: 'Company', type: 'text' },
  { id: 'address', name: 'address', label: 'Address', type: 'textarea' },
];

const defaultProduct = {
  id: '',
  name: '',
  description: '',
  price: 0,
  currency: 'USD',
  image: '',
  quantity: 1,
  isRecurring: false,
  recurringInterval: 'monthly',
};

// Custom Switch Component
const Switch = ({ checked, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={!!checked}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
      checked ? 'bg-[#6a1fbf]' : 'bg-white/20'
    }`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
        checked ? 'translate-x-5' : 'translate-x-0.5'
      }`}
    />
  </button>
);

export default function CheckoutModal({
  isOpen,
  onClose,
  onSave,
  checkout = null,
  folders = [],
  currentFolder = null,
  skipFolderSelect = false,
  readOnly = false,
  onCopyToMyElements,
}) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    folder_id: currentFolder?.id || '',
    mode: 'single',
    products: [{ ...defaultProduct, id: `prod_${Date.now()}` }],
    pricing: {
      taxEnabled: false,
      taxRate: 0,
      taxLabel: 'Tax',
      shippingEnabled: false,
      shippingOptions: [],
      discountCodesEnabled: false,
      currency: 'USD',
    },
    paymentMethods: [
      { type: 'stripe', enabled: true, config: {} },
      { type: 'paypal', enabled: false, config: {} },
      { type: 'manual', enabled: false, config: {} },
    ],
    formFields: [...DEFAULT_FIELDS],
    branding: {
      primaryColor: '#8B5CF6',
      companyName: '',
      supportEmail: '',
      termsUrl: '',
      privacyUrl: '',
    },
    confirmation: {
      heading: 'Thank you for your purchase!',
      message: 'Your order has been confirmed.',
      downloadUrl: '',
      emailReceiptEnabled: true,
    },
    discountCodes: [],
  });

  const [expandedProduct, setExpandedProduct] = useState(0);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (checkout) {
      setFormData({
        ...formData,
        ...checkout,
        folder_id: checkout.folder_id || currentFolder?.id || '',
      });
    } else {
      setFormData({
        name: '',
        folder_id: currentFolder?.id || '',
        mode: 'single',
        products: [{ ...defaultProduct, id: `prod_${Date.now()}` }],
        pricing: {
          taxEnabled: false,
          taxRate: 0,
          taxLabel: 'Tax',
          shippingEnabled: false,
          shippingOptions: [],
          discountCodesEnabled: false,
          currency: 'USD',
        },
        paymentMethods: [
          { type: 'stripe', enabled: true, config: {} },
          { type: 'paypal', enabled: false, config: {} },
          { type: 'manual', enabled: false, config: {} },
        ],
        formFields: [...DEFAULT_FIELDS],
        branding: {
          primaryColor: '#8B5CF6',
          companyName: '',
          supportEmail: '',
          termsUrl: '',
          privacyUrl: '',
        },
        confirmation: {
          heading: 'Thank you for your purchase!',
          message: 'Your order has been confirmed.',
          downloadUrl: '',
          emailReceiptEnabled: true,
        },
        discountCodes: [],
      });
      setStep(1);
    }
  }, [checkout, isOpen, currentFolder]);

  const handleImageUpload = async (productIndex, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadService.uploadFile(file, 'products');
      const updated = [...formData.products];
      updated[productIndex].image = result.url;
      setFormData({ ...formData, products: updated });
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  const updateProduct = (index, field, value) => {
    const updated = [...formData.products];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, products: updated });
  };

  const addProduct = () => {
    if (formData.products.length >= 10) return;
    setFormData({
      ...formData,
      products: [...formData.products, { ...defaultProduct, id: `prod_${Date.now()}` }],
    });
    setExpandedProduct(formData.products.length);
  };

  const removeProduct = (index) => {
    if (formData.products.length <= 1) return;
    const updated = formData.products.filter((_, i) => i !== index);
    setFormData({ ...formData, products: updated });
  };

  const togglePaymentMethod = (type) => {
    const updated = formData.paymentMethods.map(pm =>
      pm.type === type ? { ...pm, enabled: !pm.enabled } : pm
    );
    setFormData({ ...formData, paymentMethods: updated });
  };

  const addShippingOption = () => {
    const newOption = {
      id: `ship_${Date.now()}`,
      name: '',
      price: 0,
    };
    setFormData({
      ...formData,
      pricing: {
        ...formData.pricing,
        shippingOptions: [...(formData.pricing.shippingOptions || []), newOption],
      },
    });
  };

  const updateShippingOption = (index, field, value) => {
    const updated = [...formData.pricing.shippingOptions];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({
      ...formData,
      pricing: { ...formData.pricing, shippingOptions: updated },
    });
  };

  const removeShippingOption = (index) => {
    const updated = formData.pricing.shippingOptions.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      pricing: { ...formData.pricing, shippingOptions: updated },
    });
  };

  const addDiscountCode = () => {
    const newCode = {
      id: `disc_${Date.now()}`,
      code: '',
      type: 'percentage',
      value: 10,
      enabled: true,
    };
    setFormData({
      ...formData,
      discountCodes: [...formData.discountCodes, newCode],
    });
  };

  const updateDiscountCode = (index, field, value) => {
    const updated = [...formData.discountCodes];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, discountCodes: updated });
  };

  const removeDiscountCode = (index) => {
    const updated = formData.discountCodes.filter((_, i) => i !== index);
    setFormData({ ...formData, discountCodes: updated });
  };

  const toggleFormField = (fieldId) => {
    const existing = formData.formFields.find(f => f.id === fieldId);
    if (existing) {
      if (!DEFAULT_FIELDS.find(f => f.id === fieldId)) {
        setFormData({
          ...formData,
          formFields: formData.formFields.filter(f => f.id !== fieldId),
        });
      }
    } else {
      const field = OPTIONAL_FIELDS.find(f => f.id === fieldId);
      if (field) {
        setFormData({
          ...formData,
          formFields: [...formData.formFields, { ...field, required: false }],
        });
      }
    }
  };

  const handleSave = () => {
    if (!formData.name || formData.products.length === 0) return;
    if (!formData.products[0].name || formData.products[0].price <= 0) return;
    onSave(formData);
    onClose();
  };

  const canProceed = () => {
    if (step === 1) return formData.name;
    if (step === 2) return formData.mode;
    if (step === 3) return formData.products.some(p => p.name && p.price > 0);
    if (step === 4) return formData.paymentMethods.some(pm => pm.enabled);
    if (step === 5) return true;
    return false;
  };

  if (!isOpen) return null;

  const modalContent = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      style={{ zIndex: 9999 }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-3xl bg-[#001233] rounded-2xl border border-white/10 overflow-hidden max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">
              {checkout ? 'Edit Checkout' : 'Create Checkout'}
            </h2>
            <p className="text-xs text-gray-400">Step {step} of 5</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="h-1 bg-white/5">
          <div
            className="h-full bg-gradient-to-r from-purple-500 to-emerald-500 transition-all duration-300"
            style={{ width: `${(step / 5) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Read-only banner */}
          {readOnly && (
            <div className="flex items-start gap-3 mb-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
              <p className="text-sm text-purple-200">
                This element is from a shared library and is <strong>view only</strong>. Copy it to your library to make changes.
              </p>
            </div>
          )}

          {/* Step 1: Name & Folder */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-300">Checkout Name *</label>
                <input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  disabled={readOnly}
                  placeholder="e.g., Course Enrollment Checkout"
                  className={`mt-2 w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                />
              </div>

              {!skipFolderSelect && folders.length > 0 && (
                <div>
                  <label className="text-sm text-gray-300">Save to Folder</label>
                  <select
                    value={formData.folder_id}
                    onChange={(e) => setFormData({ ...formData, folder_id: e.target.value })}
                    className="mt-2 w-full h-10 rounded-md bg-white/5 border border-white/10 text-white px-3"
                  >
                    <option value="">Select folder...</option>
                    {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Choose Mode */}
          {step === 2 && (
            <div className="space-y-4">
              <label className="text-sm text-gray-300">Choose Checkout Mode</label>
              <div className="grid grid-cols-3 gap-3">
                {MODES.map((mode) => (
                  <button
                    key={mode.id}
                    onClick={() => setFormData({ ...formData, mode: mode.id })}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      formData.mode === mode.id
                        ? 'border-purple-500 bg-purple-500/10'
                        : 'border-white/10 hover:border-white/20 bg-white/5'
                    }`}
                  >
                    <mode.icon className={`w-8 h-8 mb-2 ${
                      formData.mode === mode.id ? 'text-purple-400' : 'text-gray-400'
                    }`} />
                    <p className="font-medium text-sm text-white">{mode.name}</p>
                    <p className="text-[10px] text-gray-500 mt-1">{mode.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Add Products & Pricing */}
          {step === 3 && (
            <div className="space-y-6">
              {/* Products */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm text-gray-300">Products</label>
                  {formData.mode !== 'single' && formData.products.length < 10 && (
                    <button
                      onClick={addProduct}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white/5 border border-white/20 text-white rounded-lg hover:bg-white/10"
                    >
                      <Plus className="w-3 h-3" /> Add Product
                    </button>
                  )}
                </div>

                {formData.products.map((product, idx) => (
                  <div key={product.id} className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
                    <button
                      onClick={() => setExpandedProduct(expandedProduct === idx ? -1 : idx)}
                      className="w-full p-3 flex items-center justify-between hover:bg-white/5"
                    >
                      <div className="flex items-center gap-3">
                        {product.image ? (
                          <img src={product.image} alt="" className="w-10 h-10 rounded-lg object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                            <Package className="w-5 h-5 text-gray-500" />
                          </div>
                        )}
                        <div className="text-left">
                          <p className="text-sm font-medium text-white">{product.name || `Product ${idx + 1}`}</p>
                          {product.price > 0 && (
                            <p className="text-xs text-emerald-400">${product.price}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {formData.products.length > 1 && (
                          <button
                            onClick={(e) => { e.stopPropagation(); removeProduct(idx); }}
                            className="p-1.5 hover:bg-red-500/20 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        )}
                        {expandedProduct === idx ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </div>
                    </button>

                    <AnimatePresence>
                      {expandedProduct === idx && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="border-t border-white/10"
                        >
                          <div className="p-4 space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs text-gray-400">Product Name *</label>
                                <input
                                  value={product.name}
                                  onChange={(e) => updateProduct(idx, 'name', e.target.value)}
                                  placeholder="Product name"
                                  className="mt-1 w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-gray-400">Price *</label>
                                <div className="relative mt-1">
                                  <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                  <input
                                    type="number"
                                    value={product.price || ''}
                                    onChange={(e) => updateProduct(idx, 'price', parseFloat(e.target.value) || 0)}
                                    placeholder="0.00"
                                    className="w-full pl-7 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                                  />
                                </div>
                              </div>
                            </div>

                            <div>
                              <label className="text-xs text-gray-400">Description</label>
                              <textarea
                                value={product.description}
                                onChange={(e) => updateProduct(idx, 'description', e.target.value)}
                                placeholder="Product description"
                                className="mt-1 w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm h-16 resize-none focus:outline-none"
                              />
                            </div>

                            <div>
                              <label className="text-xs text-gray-400">Image</label>
                              <div className="mt-1 flex items-center gap-3">
                                {product.image && (
                                  <img src={product.image} alt="" className="w-20 h-20 rounded-lg object-cover" />
                                )}
                                {!readOnly && (
                                  <label className="flex-1 p-3 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-purple-500/50 text-center">
                                    <input
                                      type="file"
                                      accept="image/*"
                                      className="hidden"
                                      onChange={(e) => handleImageUpload(idx, e.target.files?.[0])}
                                      disabled={uploading}
                                    />
                                    <Upload className="w-5 h-5 mx-auto text-gray-500 mb-1" />
                                    <span className="text-xs text-gray-500">Upload image</span>
                                  </label>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-4">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <Switch
                                  checked={product.isRecurring}
                                  onChange={(checked) => updateProduct(idx, 'isRecurring', checked)}
                                />
                                <span className="text-sm text-gray-300">Recurring payment</span>
                              </label>
                              {product.isRecurring && (
                                <select
                                  value={product.recurringInterval}
                                  onChange={(e) => updateProduct(idx, 'recurringInterval', e.target.value)}
                                  className="h-8 rounded-md bg-white/5 border border-white/10 text-white text-sm px-2"
                                >
                                  <option value="monthly">Monthly</option>
                                  <option value="yearly">Yearly</option>
                                </select>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>

              {/* Pricing Options */}
              <div className="space-y-4 pt-4 border-t border-white/10">
                <label className="text-sm text-gray-300">Pricing Options</label>

                <div className="space-y-3">
                  <label className="flex items-center justify-between p-3 bg-white/5 rounded-lg cursor-pointer">
                    <span className="text-sm text-gray-300">Enable Tax</span>
                    <Switch
                      checked={formData.pricing.taxEnabled}
                      onChange={(checked) => setFormData({
                        ...formData,
                        pricing: { ...formData.pricing, taxEnabled: checked }
                      })}
                    />
                  </label>

                  {formData.pricing.taxEnabled && (
                    <div className="grid grid-cols-2 gap-3 pl-4">
                      <div>
                        <label className="text-xs text-gray-400">Tax Rate (%)</label>
                        <input
                          type="number"
                          value={formData.pricing.taxRate || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            pricing: { ...formData.pricing, taxRate: parseFloat(e.target.value) || 0 }
                          })}
                          className="mt-1 w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400">Tax Label</label>
                        <input
                          value={formData.pricing.taxLabel}
                          onChange={(e) => setFormData({
                            ...formData,
                            pricing: { ...formData.pricing, taxLabel: e.target.value }
                          })}
                          className="mt-1 w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                        />
                      </div>
                    </div>
                  )}

                  <label className="flex items-center justify-between p-3 bg-white/5 rounded-lg cursor-pointer">
                    <span className="text-sm text-gray-300">Enable Shipping</span>
                    <Switch
                      checked={formData.pricing.shippingEnabled}
                      onChange={(checked) => setFormData({
                        ...formData,
                        pricing: { ...formData.pricing, shippingEnabled: checked }
                      })}
                    />
                  </label>

                  {formData.pricing.shippingEnabled && (
                    <div className="pl-4 space-y-2">
                      {formData.pricing.shippingOptions?.map((opt, idx) => (
                        <div key={opt.id} className="flex items-center gap-2">
                          <input
                            value={opt.name}
                            onChange={(e) => updateShippingOption(idx, 'name', e.target.value)}
                            placeholder="Option name"
                            className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                          />
                          <input
                            type="number"
                            value={opt.price || ''}
                            onChange={(e) => updateShippingOption(idx, 'price', parseFloat(e.target.value) || 0)}
                            placeholder="Price"
                            className="w-24 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                          />
                          <button onClick={() => removeShippingOption(idx)} className="p-2 hover:bg-red-500/20 rounded-lg">
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={addShippingOption}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white/5 border border-white/20 text-white rounded-lg hover:bg-white/10"
                      >
                        <Plus className="w-3 h-3" /> Add Shipping Option
                      </button>
                    </div>
                  )}

                  <label className="flex items-center justify-between p-3 bg-white/5 rounded-lg cursor-pointer">
                    <span className="text-sm text-gray-300">Enable Discount Codes</span>
                    <Switch
                      checked={formData.pricing.discountCodesEnabled}
                      onChange={(checked) => setFormData({
                        ...formData,
                        pricing: { ...formData.pricing, discountCodesEnabled: checked }
                      })}
                    />
                  </label>

                  {formData.pricing.discountCodesEnabled && (
                    <div className="pl-4 space-y-2">
                      {formData.discountCodes.map((code, idx) => (
                        <div key={code.id} className="flex items-center gap-2">
                          <input
                            value={code.code}
                            onChange={(e) => updateDiscountCode(idx, 'code', e.target.value.toUpperCase())}
                            placeholder="CODE"
                            className="w-28 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm uppercase focus:outline-none"
                          />
                          <select
                            value={code.type}
                            onChange={(e) => updateDiscountCode(idx, 'type', e.target.value)}
                            className="h-9 rounded-md bg-white/5 border border-white/10 text-white text-sm px-2"
                          >
                            <option value="percentage">%</option>
                            <option value="fixed">$</option>
                          </select>
                          <input
                            type="number"
                            value={code.value || ''}
                            onChange={(e) => updateDiscountCode(idx, 'value', parseFloat(e.target.value) || 0)}
                            placeholder="Value"
                            className="w-20 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                          />
                          <button onClick={() => removeDiscountCode(idx)} className="p-2 hover:bg-red-500/20 rounded-lg">
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={addDiscountCode}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white/5 border border-white/20 text-white rounded-lg hover:bg-white/10"
                      >
                        <Plus className="w-3 h-3" /> Add Discount Code
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Payment Methods & Form Fields */}
          {step === 4 && (
            <div className="space-y-6">
              {/* Payment Methods */}
              <div className="space-y-4">
                <label className="text-sm text-gray-300">Payment Methods</label>
                <div className="space-y-2">
                  {PAYMENT_METHODS.map((method) => {
                    const enabled = formData.paymentMethods.find(pm => pm.type === method.id)?.enabled;
                    return (
                      <label
                        key={method.id}
                        className={`flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          enabled ? 'border-purple-500 bg-purple-500/10' : 'border-white/10 bg-white/5'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <method.icon className={`w-5 h-5 ${enabled ? 'text-purple-400' : 'text-gray-400'}`} />
                          <div>
                            <p className="text-sm font-medium text-white">{method.name}</p>
                            <p className="text-[10px] text-gray-500">{method.desc}</p>
                          </div>
                        </div>
                        <Switch
                          checked={enabled}
                          onChange={() => togglePaymentMethod(method.id)}
                        />
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Form Fields */}
              <div className="space-y-4 pt-4 border-t border-white/10">
                <label className="text-sm text-gray-300">Customer Information Fields</label>

                <div className="space-y-2">
                  {DEFAULT_FIELDS.map((field) => (
                    <div key={field.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <span className="text-sm text-gray-300">{field.label}</span>
                      <span className="text-xs text-purple-400">Required</span>
                    </div>
                  ))}

                  {OPTIONAL_FIELDS.map((field) => {
                    const enabled = formData.formFields.some(f => f.id === field.id);
                    return (
                      <label
                        key={field.id}
                        className="flex items-center justify-between p-3 bg-white/5 rounded-lg cursor-pointer"
                      >
                        <span className="text-sm text-gray-300">{field.label}</span>
                        <Switch
                          checked={enabled}
                          onChange={() => toggleFormField(field.id)}
                        />
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Branding & Confirmation */}
          {step === 5 && (
            <div className="space-y-6">
              {/* Branding */}
              <div className="space-y-4">
                <label className="text-sm text-gray-300">Branding</label>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-400">Company Name</label>
                    <input
                      value={formData.branding.companyName}
                      onChange={(e) => setFormData({
                        ...formData,
                        branding: { ...formData.branding, companyName: e.target.value }
                      })}
                      className="mt-1 w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400">Support Email</label>
                    <input
                      value={formData.branding.supportEmail}
                      onChange={(e) => setFormData({
                        ...formData,
                        branding: { ...formData.branding, supportEmail: e.target.value }
                      })}
                      className="mt-1 w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-400">Primary Color</label>
                  <div className="mt-1 flex gap-2">
                    <input
                      type="color"
                      value={formData.branding.primaryColor}
                      onChange={(e) => setFormData({
                        ...formData,
                        branding: { ...formData.branding, primaryColor: e.target.value }
                      })}
                      className="w-9 h-9 rounded cursor-pointer border-0"
                    />
                    <input
                      value={formData.branding.primaryColor}
                      onChange={(e) => setFormData({
                        ...formData,
                        branding: { ...formData.branding, primaryColor: e.target.value }
                      })}
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-400">Terms URL</label>
                    <input
                      value={formData.branding.termsUrl}
                      onChange={(e) => setFormData({
                        ...formData,
                        branding: { ...formData.branding, termsUrl: e.target.value }
                      })}
                      placeholder="https://..."
                      className="mt-1 w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400">Privacy URL</label>
                    <input
                      value={formData.branding.privacyUrl}
                      onChange={(e) => setFormData({
                        ...formData,
                        branding: { ...formData.branding, privacyUrl: e.target.value }
                      })}
                      placeholder="https://..."
                      className="mt-1 w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Confirmation */}
              <div className="space-y-4 pt-4 border-t border-white/10">
                <label className="text-sm text-gray-300">Confirmation Page</label>

                <div>
                  <label className="text-xs text-gray-400">Success Heading</label>
                  <input
                    value={formData.confirmation.heading}
                    onChange={(e) => setFormData({
                      ...formData,
                      confirmation: { ...formData.confirmation, heading: e.target.value }
                    })}
                    className="mt-1 w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                  />
                </div>

                <div>
                  <label className="text-xs text-gray-400">Success Message</label>
                  <textarea
                    value={formData.confirmation.message}
                    onChange={(e) => setFormData({
                      ...formData,
                      confirmation: { ...formData.confirmation, message: e.target.value }
                    })}
                    className="mt-1 w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm h-16 resize-none focus:outline-none"
                  />
                </div>

                <div>
                  <label className="text-xs text-gray-400">Download URL (for digital products)</label>
                  <input
                    value={formData.confirmation.downloadUrl}
                    onChange={(e) => setFormData({
                      ...formData,
                      confirmation: { ...formData.confirmation, downloadUrl: e.target.value }
                    })}
                    placeholder="https://..."
                    className="mt-1 w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                  />
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch
                    checked={formData.confirmation.emailReceiptEnabled}
                    onChange={(checked) => setFormData({
                      ...formData,
                      confirmation: { ...formData.confirmation, emailReceiptEnabled: checked }
                    })}
                  />
                  <span className="text-sm text-gray-300">Send email receipt</span>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 flex justify-between">
          <button
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            className="px-4 py-2 bg-white/5 border border-white/20 text-white rounded-lg hover:bg-white/10 transition-colors"
          >
            {step === 1 ? 'Cancel' : 'Back'}
          </button>
          {step < 5 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="px-4 py-2 bg-gradient-to-r from-purple-500 to-emerald-500 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Continue
            </button>
          ) : readOnly ? (onCopyToMyElements && (
            <button
              type="button"
              onClick={onCopyToMyElements}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-white hover:opacity-90 transition-all"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
              Copy to My Elements
            </button>
          )) : (
            <button
              onClick={handleSave}
              disabled={!canProceed()}
              className="px-4 py-2 bg-gradient-to-r from-purple-500 to-emerald-500 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Save Checkout
            </button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
